import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  IconButton,
  Rating,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

import AuthModal from '../../Auth/AuthModal';
import { useClientChat } from '../../client/messaging/widget/ClientChatContext';
import ReviewModal from '../../client/reviews/ReviewModal';
import SalonPublicButton from '../../common/salonPublicAppearance/SalonPublicButton';
import SalonTemplateAbout from '../../common/salonPublicTemplates/blocks/SalonTemplateAbout';
import SalonTemplateOpeningHours from '../../common/salonPublicTemplates/blocks/SalonTemplateOpeningHours';
import SalonTemplateRenderer from '../../common/salonPublicTemplates/SalonTemplateRenderer';
import { buildSalonTemplateViewModel } from '../../common/salonPublicTemplates/salonTemplateViewModel';
import { useAuth } from '../../../context/AuthContext';
import { useSalonPublicTheme } from '../../../context/SalonPublicThemeContext';
import ReviewService from '../../../services/review.service';
import { axiosClient } from '../../../services/axiosClient';
import {
  useSalonsCategoriesListQuery,
  useSalonsRetrieveQuery,
  useServicesPublicSalonListQuery,
} from '../../../services/GLOWClient';
import {
  useCheckFavoriteQuery,
  useSalonPublicClosuresListQuery,
  useToggleFavoriteMutation,
} from '../../../services/salonsApi';
import { HIGH_DENSITY_TABLET_MEDIA } from '../../../utils/responsiveMedia';


const Button = (props) => <SalonPublicButton preserveLegacyStyle {...props} />;

const AllReviewsDialog = ({ open, onClose, reviews, averageRating }) => {
  const { t } = useTranslation();
  const { tokens: theme } = useSalonPublicTheme();
  const items = Array.isArray(reviews) ? reviews : [];

  return (
    <Dialog open={Boolean(open)} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, color: theme.colors.gray[700] }}>
        {t('landing_salon_details.all_reviews')}
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        {typeof averageRating === 'number' ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Rating value={averageRating} precision={0.5} readOnly size="small" />
            <Typography sx={{ color: theme.colors.gray[600], fontWeight: 600 }}>
              {averageRating.toFixed(1)}
            </Typography>
          </Box>
        ) : null}
        {items.length === 0 ? (
          <Typography sx={{ color: theme.colors.gray[600] }}>
            {t('landing_salon_details.no_reviews_yet')}
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {items.map((review, index) => (
              <Box
                key={review?.review_id || review?.id || index}
                sx={{ border: `1px solid ${theme.colors.border.light}`, borderRadius: 3, p: 2 }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 1 }}>
                  <Typography sx={{ fontWeight: 700, color: theme.colors.gray[700] }}>
                    {review?.client_name || review?.user_name || review?.author || t('landing_salon_details.client')}
                  </Typography>
                  <Rating value={Number(review?.rating || 0)} readOnly size="small" />
                </Box>
                {review?.comment || review?.content ? (
                  <Typography sx={{ color: theme.colors.gray[600], lineHeight: 1.6 }}>
                    {review?.comment || review?.content}
                  </Typography>
                ) : null}
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button
          onClick={onClose}
          sx={{ fontWeight: 700, textTransform: 'none', color: theme.colors.primary[600] }}
        >
          {t('common.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const MobileHeaderInfoDialog = ({ open, onClose, identity }) => {
  const { t } = useTranslation();
  const { tokens: theme } = useSalonPublicTheme();

  return (
    <Dialog open={Boolean(open)} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 600, color: theme.colors.gray[700] }}>
        {t('landing_salon_details.salon_info')}
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {identity?.address ? (
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
              <LocationOnIcon sx={{ color: theme.colors.primary[600], mt: 0.5 }} />
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: theme.colors.gray[700] }}>
                  {t('landing_salon_details.address')}
                </Typography>
                <Typography variant="body2" sx={{ color: theme.colors.gray[600] }}>
                  {identity.address}
                </Typography>
              </Box>
            </Box>
          ) : null}
          {identity?.phone ? (
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
              <PhoneIcon sx={{ color: theme.colors.primary[600], mt: 0.5 }} />
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: theme.colors.gray[700] }}>
                  {t('landing_salon_details.phone')}
                </Typography>
                <Typography
                  component="a"
                  href={`tel:${identity.phone}`}
                  variant="body2"
                  sx={{ color: theme.colors.primary[600], textDecoration: 'none', fontWeight: 500 }}
                >
                  {identity.phone}
                </Typography>
              </Box>
            </Box>
          ) : null}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 2 }}>
        <Button onClick={onClose}>{t('landing_salon_details.close')}</Button>
      </DialogActions>
    </Dialog>
  );
};

const MobileInformationDrawer = ({ open, onClose, viewModel }) => {
  const { t } = useTranslation();
  const { layout, tokens: theme } = useSalonPublicTheme();

  return (
    <Drawer
      anchor="right"
      open={Boolean(open)}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100vw', sm: 400 },
          maxWidth: '100vw',
          backgroundColor: theme.salon.background,
          borderLeft: `1px solid ${theme.colors.border.light}`,
        },
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 3,
            borderBottom: `1px solid ${theme.colors.border.light}`,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, color: theme.colors.gray[700] }}>
            {t('landing_salon_details.informations')}
          </Typography>
          <IconButton aria-label={t('common.close')} onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Box sx={{ flex: 1, overflowY: 'auto', p: 2, pr: 7 }}>
          <SalonTemplateOpeningHours
            data={{ hours: viewModel?.openingHours, closures: [] }}
            visible={layout.tokens.visibility.opening_hours}
            includeClosures={false}
          />
          <SalonTemplateAbout data={viewModel?.identity} />
        </Box>
      </Box>
    </Drawer>
  );
};

const TeamMemberServicesDialog = ({ member, onClose }) => {
  const { t } = useTranslation();
  const { tokens: theme } = useSalonPublicTheme();
  const open = Boolean(member);
  const initials = `${member?.firstName?.[0] || '?'}${member?.lastName?.[0] || ''}`.toUpperCase();
  const formatPrice = (service, price) => {
    const amount = `${price} ${t('landing_salon_details.currency')}`;
    return service.isFixedPrice === false
      ? `${t('landing_salon_details.starting_from')} ${amount}`
      : amount;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.12)' } }}
    >
      <DialogTitle
        sx={{
          background: `linear-gradient(135deg, ${theme.colors.primary[500]} 0%, ${theme.colors.primary[600]} 100%)`,
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 2.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 60,
              height: 60,
              borderRadius: '50%',
              backgroundColor: 'rgba(255,255,255,0.2)',
              display: 'grid',
              placeItems: 'center',
              overflow: 'hidden',
              border: '2px solid rgba(255,255,255,0.5)',
            }}
          >
            {member?.photo ? (
              <Box component="img" src={member.photo} alt={member.name} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <Typography variant="h5" sx={{ fontWeight: 600, color: 'white' }}>{initials}</Typography>
            )}
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {member?.name || t('landing_salon_details.team_member')}
          </Typography>
        </Box>
        <IconButton
          aria-label={t('common.close')}
          onClick={onClose}
          sx={{ color: 'white', '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 3, mt: 2 }}>
        {member?.services?.length ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme.colors.gray[700], mb: 1 }}>
              {t('landing_salon_details.services_offered')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {member.services.map((service) => (
                <Box
                  key={service.id}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: theme.colors.primary[50],
                    border: `1px solid ${theme.colors.primary[200]}`,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    transition: 'all 0.2s ease',
                    '&:hover': { backgroundColor: theme.colors.primary[100], transform: 'translateX(4px)' },
                  }}
                >
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: theme.colors.primary[500], flexShrink: 0 }} />
                  <Typography variant="body1" sx={{ fontWeight: 500, color: theme.colors.gray[700], flex: 1 }}>
                    {service.name}
                  </Typography>
                  {service.price != null ? (
                    service.promotion ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.25 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: theme.colors.gray[500], textDecoration: 'line-through' }}>
                          {formatPrice(service, service.price)}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 800, color: theme.colors.primary[700] }}>
                          {formatPrice(service, service.promotion.discountedPrice)}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2" sx={{ fontWeight: 600, color: theme.colors.primary[600] }}>
                        {formatPrice(service, service.price)}
                      </Typography>
                    )
                  ) : null}
                </Box>
              ))}
            </Box>
          </Box>
        ) : (
          <Typography variant="body1" sx={{ color: theme.colors.gray[500], textAlign: 'center', py: 3 }}>
            {t('landing_salon_details.no_services_available')}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            borderColor: theme.colors.primary[300],
            color: theme.colors.primary[600],
            fontWeight: 600,
            px: 3,
            py: 1,
            borderRadius: 2,
            textTransform: 'none',
            '&:hover': { borderColor: theme.colors.primary[500], backgroundColor: theme.colors.primary[50] },
          }}
        >
          {t('common.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const PhotoLightboxDialog = ({ photos, index, open, onClose, onSelect }) => {
  const { t } = useTranslation();
  const items = Array.isArray(photos) ? photos : [];
  const photo = items[index];

  if (!open || !photo) return null;

  return (
    <Box
      data-testid="salon-photo-lightbox"
      onClick={onClose}
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backdropFilter: 'blur(10px)',
      }}
    >
      <IconButton
        aria-label={t('common.close')}
        onClick={(event) => {
          event.stopPropagation();
          onClose();
        }}
        sx={{
          position: 'absolute',
          top: 20,
          right: 20,
          color: 'white',
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(10px)',
          '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.2)' },
        }}
      >
        <CloseIcon />
      </IconButton>

      <Box
        sx={{
          position: 'absolute',
          top: 20,
          left: 20,
          color: 'white',
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          px: 2,
          py: 1,
          borderRadius: 2,
          backdropFilter: 'blur(10px)',
        }}
      >
        {index + 1} / {items.length}
      </Box>

      {items.length > 1 ? (
        <IconButton
          aria-label={t('actions.previous')}
          onClick={(event) => {
            event.stopPropagation();
            onSelect((index - 1 + items.length) % items.length);
          }}
          sx={{
            position: 'absolute',
            left: 20,
            top: '50%',
            transform: 'translateY(-50%)',
            color: 'white',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(10px)',
            width: 56,
            height: 56,
            '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.7)' },
          }}
        >
          <ChevronLeftIcon sx={{ fontSize: '2rem' }} />
        </IconButton>
      ) : null}

      <Box
        onClick={(event) => event.stopPropagation()}
        sx={{
          maxWidth: '90vw',
          maxHeight: '90vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          component="img"
          src={photo.src}
          alt={photo.alt}
          sx={{
            maxWidth: '100%',
            maxHeight: '100%',
            objectFit: 'contain',
            borderRadius: 2,
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
          }}
        />
      </Box>

      {items.length > 1 ? (
        <IconButton
          aria-label={t('actions.next')}
          onClick={(event) => {
            event.stopPropagation();
            onSelect((index + 1) % items.length);
          }}
          sx={{
            position: 'absolute',
            right: 20,
            top: '50%',
            transform: 'translateY(-50%)',
            color: 'white',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(10px)',
            width: 56,
            height: 56,
            '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.7)' },
          }}
        >
          <ChevronRightIcon sx={{ fontSize: '2rem' }} />
        </IconButton>
      ) : null}
    </Box>
  );
};

const SalonDetails = () => {
  const { t, i18n } = useTranslation();
  const { layout, templateKey, tokens: theme } = useSalonPublicTheme();
  const { salonId } = useParams();
  const navigate = useNavigate();
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const usesDocumentScroll = isMobile
    || ['pure_studio', 'neo_derma', 'gloss_lab'].includes(templateKey);
  const { isAuthenticated } = useAuth();
  const { openForSalon, setContextSalon } = useClientChat();
  const parsedSalonId = Number.parseInt(salonId, 10);
  const authenticated = isAuthenticated();
  const reviewsLoadErrorLabel = t('landing_salon_details.reviews_load_error');

  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [pendingFavoriteAction, setPendingFavoriteAction] = useState(null);
  const [pendingContactAction, setPendingContactAction] = useState(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [mobileHeaderDialogOpen, setMobileHeaderDialogOpen] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewsError, setReviewsError] = useState(null);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [allReviewsDialogOpen, setAllReviewsDialogOpen] = useState(false);
  const [selectedTeamMember, setSelectedTeamMember] = useState(null);
  const [photoIndex, setPhotoIndex] = useState(0);
  const [photoDialogOpen, setPhotoDialogOpen] = useState(false);
  const [promoBanner, setPromoBanner] = useState(null);
  const [publicPromotions, setPublicPromotions] = useState([]);

  const { data: favoriteStatus, refetch: refetchFavoriteStatus } = useCheckFavoriteQuery(
    parsedSalonId,
    { skip: !authenticated || !parsedSalonId },
  );
  const [toggleFavorite] = useToggleFavoriteMutation();
  const isFavorite = Boolean(favoriteStatus?.is_favorite);

  const { data: salonDetails, isLoading: detailsLoading } = useSalonsRetrieveQuery(
    { salonId: parsedSalonId, language: i18n.language },
    { skip: !parsedSalonId },
  );
  const { data: categoriesData, isLoading: categoriesLoading } = useSalonsCategoriesListQuery(
    { salonId: parsedSalonId },
    { skip: !parsedSalonId },
  );
  const { data: servicesData, isLoading: servicesLoading } = useServicesPublicSalonListQuery(
    { salonId: parsedSalonId },
    { skip: !parsedSalonId },
  );
  const {
    data: closuresData,
    isLoading: closuresLoading,
    error: closuresError,
  } = useSalonPublicClosuresListQuery(
    { salonId: parsedSalonId },
    { skip: !parsedSalonId },
  );

  const mergedSalon = useMemo(() => salonDetails || {}, [salonDetails]);

  useEffect(() => {
    if (!mergedSalon?.salon_id) return undefined;
    setContextSalon(mergedSalon);
    return () => setContextSalon(null);
  }, [mergedSalon, setContextSalon]);

  const openingHoursRaw = useMemo(() => (
    mergedSalon?.opening_hours
    ?? mergedSalon?.openingHours
    ?? mergedSalon?.hours
    ?? mergedSalon?.schedule
    ?? mergedSalon?.horaires
    ?? mergedSalon?.opening_hours_text
    ?? mergedSalon?.openingHoursText
    ?? null
  ), [mergedSalon]);

  useEffect(() => {
    const candidates = [
      ...(Array.isArray(mergedSalon?.photos) ? mergedSalon.photos : []),
      mergedSalon?.cover_photo,
      mergedSalon?.photo,
      mergedSalon?.image,
    ].filter(Boolean);
    const primaryIndex = candidates.findIndex((photo) => (
      typeof photo === 'object' && photo?.is_primary
    ));
    setPhotoIndex(primaryIndex >= 0 ? primaryIndex : 0);
  }, [mergedSalon]);

  useEffect(() => {
    if (!parsedSalonId) return undefined;
    let cancelled = false;

    axiosClient.get(`promotions/public/salon/${parsedSalonId}/banner`)
      .then((response) => {
        if (!cancelled) setPromoBanner(response?.data?.banner || null);
      })
      .catch(() => {
        if (!cancelled) setPromoBanner(null);
      });
    axiosClient.get(`promotions/public/salon/${parsedSalonId}/`)
      .then((response) => {
        if (cancelled) return;
        setPublicPromotions(Array.isArray(response?.data)
          ? response.data
          : response?.data?.results || []);
      })
      .catch(() => {
        if (!cancelled) setPublicPromotions([]);
      });

    return () => { cancelled = true; };
  }, [parsedSalonId]);

  const loadReviews = useCallback(async () => {
    if (!salonId) return;
    setReviewsLoading(true);
    setReviewsError(null);
    try {
      const data = await ReviewService.getSalonReviews(salonId);
      setReviews(Array.isArray(data) ? data : data?.results || []);
    } catch (_error) {
      setReviewsError(reviewsLoadErrorLabel);
    } finally {
      setReviewsLoading(false);
    }
  }, [reviewsLoadErrorLabel, salonId]);

  useEffect(() => {
    loadReviews();
  }, [loadReviews]);

  const handleBack = useCallback(() => navigate('/'), [navigate]);
  const handleBookAppointment = useCallback((service) => {
    if (service?.selectedTime) {
      setSelectedService(service);
      return;
    }
    navigate(`/salon/${salonId}/services?service=${service?.service_id}&startBooking=true`);
  }, [navigate, salonId]);
  const handleFavoriteToggle = useCallback(async () => {
    try {
      await toggleFavorite(parsedSalonId).unwrap();
      refetchFavoriteStatus();
    } catch (_error) {
      // Existing behavior intentionally leaves the public page usable on failure.
    }
  }, [parsedSalonId, refetchFavoriteStatus, toggleFavorite]);
  const handleFavoriteClick = useCallback(async () => {
    if (!isAuthenticated()) {
      setPendingFavoriteAction('toggle');
      setAuthModalOpen(true);
      return;
    }
    await handleFavoriteToggle();
  }, [handleFavoriteToggle, isAuthenticated]);
  const handleAuthModalClose = useCallback(() => {
    setAuthModalOpen(false);
    setSelectedService(null);
    setPendingContactAction(false);
  }, []);
  const handleAuthSuccess = useCallback((userData) => {
    if (userData || isAuthenticated()) {
      if (selectedService) {
        navigate(`/salons/${salonId}?service=${selectedService.service_id}`);
      }
      if (pendingFavoriteAction === 'toggle') handleFavoriteToggle();
      if (pendingContactAction) openForSalon(mergedSalon);
    }
    setAuthModalOpen(false);
    setSelectedService(null);
    setPendingFavoriteAction(null);
    setPendingContactAction(false);
  }, [
    handleFavoriteToggle,
    isAuthenticated,
    mergedSalon,
    navigate,
    openForSalon,
    pendingContactAction,
    pendingFavoriteAction,
    salonId,
    selectedService,
  ]);

  const handleContactSalon = useCallback(() => {
    if (!isAuthenticated()) {
      setPendingContactAction(true);
      setAuthModalOpen(true);
      return;
    }
    openForSalon(mergedSalon);
  }, [isAuthenticated, mergedSalon, openForSalon]);
  const handleOpenReviewModal = useCallback(() => {
    if (!isAuthenticated()) {
      setAuthModalOpen(true);
      return;
    }
    setReviewModalOpen(true);
  }, [isAuthenticated]);
  const handleReviewSuccess = useCallback(() => {
    loadReviews();
    setReviewModalOpen(false);
  }, [loadReviews]);
  const handleSelectPhoto = useCallback((index) => {
    setPhotoIndex(index);
    setPhotoDialogOpen(true);
  }, []);
  const handleOpenBooking = useCallback(() => {
    document.getElementById('services')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const displayedAverageRating = useMemo(() => {
    if (reviews.length === 0) return null;
    return reviews.reduce((sum, review) => sum + (review.rating || 0), 0) / reviews.length;
  }, [reviews]);

  const templateViewModel = useMemo(() => {
    const normalized = buildSalonTemplateViewModel({
      salon: mergedSalon,
      categories: categoriesData,
      services: servicesData,
      promotions: publicPromotions,
      team: mergedSalon.employees,
      reviews,
      openingHours: openingHoursRaw,
      closures: closuresLoading || closuresError ? [] : closuresData,
      language: i18n.language,
      canClientBook: true,
      isFavorite,
    });
    return {
      ...normalized,
      averageRating: displayedAverageRating,
      publicState: {
        isAuthenticated: authenticated,
        reviewsLoading,
        reviewsError,
        servicesLoading: categoriesLoading || servicesLoading,
        promoBanner,
        photoIndex,
      },
    };
  }, [
    authenticated,
    categoriesData,
    categoriesLoading,
    closuresData,
    closuresError,
    closuresLoading,
    displayedAverageRating,
    i18n.language,
    isFavorite,
    mergedSalon,
    openingHoursRaw,
    photoIndex,
    promoBanner,
    publicPromotions,
    reviews,
    reviewsError,
    reviewsLoading,
    servicesData,
    servicesLoading,
  ]);

  const handleSelectTeamMember = useCallback((rawMember) => {
    const member = templateViewModel.team.find((item) => item.raw === rawMember);
    setSelectedTeamMember(member || null);
  }, [templateViewModel.team]);

  const templateActions = useMemo(() => ({
    onBack: handleBack,
    onToggleFavorite: handleFavoriteClick,
    onBookService: handleBookAppointment,
    onOpenMobileInfo: () => setMobileDrawerOpen(true),
    onOpenHeaderInfo: () => setMobileHeaderDialogOpen(true),
    onOpenReviewComposer: handleOpenReviewModal,
    onOpenAllReviews: () => setAllReviewsDialogOpen(true),
    onSelectPhoto: handleSelectPhoto,
    onNavigatePhoto: setPhotoIndex,
    onSelectTeamMember: handleSelectTeamMember,
    onOpenBooking: handleOpenBooking,
    onContact: handleContactSalon,
  }), [
    handleBack,
    handleFavoriteClick,
    handleOpenBooking,
    handleOpenReviewModal,
    handleBookAppointment,
    handleContactSalon,
    handleSelectPhoto,
    handleSelectTeamMember,
  ]);

  if (detailsLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '50vh',
          backgroundColor: theme.colors.secondary[100],
          borderRadius: 2,
          my: 4,
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={40} sx={{ color: theme.colors.primary[600], mb: 2 }} />
          <Typography variant="body2" sx={{ color: theme.colors.gray[500], fontWeight: 500 }}>
            {t('common.loading')}
          </Typography>
        </Box>
      </Box>
    );
  }

  const isSalonUnavailable = salonDetails?.is_visible === false
    || (typeof salonDetails?.detail === 'string' && !salonDetails?.name);
  if (isSalonUnavailable) {
    const message = salonDetails?.detail || t('landing_salon_details.salon_not_active');
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: theme.salon.background,
          px: { xs: 2, md: 3 },
          py: { xs: 4, md: 6 },
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: 720,
            backgroundColor: theme.colors.background.primary,
            borderRadius: 4,
            border: `1px solid ${theme.colors.border.light}`,
            boxShadow: `0 12px 32px ${alpha(theme.salon.primary, 0.12)}`,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              px: { xs: 3, md: 5 },
              py: { xs: 3, md: 4 },
              background: `linear-gradient(135deg, ${theme.colors.primary[600]} 0%, ${theme.colors.secondary[600]} 100%)`,
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Box
              sx={{
                width: 52,
                height: 52,
                borderRadius: '16px',
                backgroundColor: 'rgba(255,255,255,0.18)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid rgba(255,255,255,0.25)',
              }}
            >
              <WarningAmberIcon sx={{ fontSize: '1.8rem', color: 'white' }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 900, fontSize: { xs: '1.1rem', md: '1.25rem' }, lineHeight: 1.2 }}>
                {t('landing_salon_details.salon_unavailable_title')}
              </Typography>
              <Typography sx={{ opacity: 0.95, fontWeight: 600, fontSize: { xs: '0.9rem', md: '0.95rem' } }}>
                {t('landing_salon_details.salon_unavailable_subtitle')}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ px: { xs: 3, md: 5 }, py: { xs: 3, md: 4 } }}>
            <Typography sx={{ fontWeight: 800, color: theme.colors.gray[700], mb: 1 }}>{message}</Typography>
            <Typography sx={{ color: theme.colors.gray[500], fontWeight: 600, lineHeight: 1.6 }}>
              {t('landing_salon_details.salon_no_subscription')}
            </Typography>
            <Box sx={{ mt: 3, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                onClick={() => navigate('/search-salon')}
                sx={{
                  textTransform: 'none',
                  fontWeight: 800,
                  borderRadius: 2.5,
                  px: 2.5,
                  backgroundColor: theme.salon.primary,
                  color: theme.salon.compatibilityMode ? 'white' : theme.salon.onPrimary,
                  '&:hover': { backgroundColor: theme.colors.primary[700] },
                }}
              >
                {t('landing_salon_details.search_another_salon')}
              </Button>
              <Button
                variant="outlined"
                onClick={handleBack}
                sx={{
                  textTransform: 'none',
                  fontWeight: 800,
                  borderRadius: 2.5,
                  px: 2.5,
                  color: theme.colors.primary[700],
                  borderColor: theme.colors.primary[200],
                  '&:hover': {
                    borderColor: theme.colors.primary[400],
                    backgroundColor: alpha(theme.salon.primary, 0.06),
                  },
                }}
              >
                {t('landing_salon_details.back')}
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      data-testid="salon-public-theme-root"
      data-scroll-container={usesDocumentScroll ? 'document' : 'content'}
      style={{
        backgroundColor: theme.salon.background,
        fontFamily: theme.typography.fontFamily.sans.join(', '),
      }}
      sx={{
        height: usesDocumentScroll ? 'auto' : '100vh',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        overflow: usesDocumentScroll ? 'visible' : 'hidden',
        [HIGH_DENSITY_TABLET_MEDIA]: { height: 'auto', overflow: 'visible' },
      }}
    >
      <SalonTemplateRenderer
        templateKey={templateKey}
        viewModel={templateViewModel}
        actions={templateActions}
        viewport={isMobile ? 'mobile' : 'desktop'}
        direction={layout.tokens.direction}
      />

      <MobileHeaderInfoDialog
        open={mobileHeaderDialogOpen}
        onClose={() => setMobileHeaderDialogOpen(false)}
        identity={templateViewModel.identity}
      />
      <MobileInformationDrawer
        open={mobileDrawerOpen}
        onClose={() => setMobileDrawerOpen(false)}
        viewModel={templateViewModel}
      />
      <PhotoLightboxDialog
        photos={templateViewModel.photos}
        index={photoIndex}
        open={photoDialogOpen}
        onClose={() => setPhotoDialogOpen(false)}
        onSelect={setPhotoIndex}
      />
      <TeamMemberServicesDialog
        member={selectedTeamMember}
        onClose={() => setSelectedTeamMember(null)}
      />
      <AllReviewsDialog
        open={allReviewsDialogOpen}
        onClose={() => setAllReviewsDialogOpen(false)}
        reviews={reviews}
        averageRating={displayedAverageRating}
      />
      <AuthModal
        open={authModalOpen}
        onClose={handleAuthModalClose}
        userType="client"
        onAuthSuccess={handleAuthSuccess}
      />
      <ReviewModal
        open={reviewModalOpen}
        onClose={() => setReviewModalOpen(false)}
        salonId={salonId}
        salonName={templateViewModel.identity.name}
        onSuccess={handleReviewSuccess}
      />
    </Box>
  );
};

export default SalonDetails;
